import { BannerImageCropType } from 'utils/constants/banner';
import { removeBannerFromState } from 'utils/moderation';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export enum AdvertisingServiceType {
    Information = 'INFORMATION',
    Advertising = 'ADVERTISING',
}

export enum LastModerationStatus {
    NeedModeration = 'NEED_MODERATION',
    Approved = 'APPROVED',
    Rejected = 'REJECTED',
}

export enum RejectReasonAvailabilityType {
    All = 'ALL',
    Advertisement = 'ADVERTISEMENT',
    Information = 'INFORMATION',
}

export interface BannerModerationInfo {
    bannerId: number;
    title: string;
    text: string;
    advertisementUrl: string;
    advertisingServiceType: AdvertisingServiceType;
    imageUrls: Record<BannerImageCropType, string>;
    video: {
        width: number;
        height: number;
        thumbnailUrl: string;
        videoUrl: string;
    } | null;
    needModeration: boolean;
    lastModeration: {
        lastModerationTime: string;
        moderatorName: string;
        rejectReasons: number[];
        comment: string;
        moderationStatus: LastModerationStatus;
    } | null;
    moderationWaitTimeInMinutes: number;
}

export interface CampaignModerationInfo {
    campaignId: number;
    campaignName: string;
    banners: BannerModerationInfo[];
}

export interface ModerationReason {
    id: number;
    title: string;
    defaultComment: string;
    description: string | null;
    rejectReasonAvailabilityType: RejectReasonAvailabilityType;
}

// ----------------------- actions -------------------------------------
const SET_CAMPAIGN_MODERATION_LIST = 'SET_CAMPAIGN_MODERATION_LIST';
const SET_BANNER_MODERATION_INFO = 'SET_BANNER_MODERATION_INFO';
const REMOVE_BANNER_FROM_MODERATION_LIST = 'REMOVE_BANNER_FROM_MODERATION_LIST';
const SET_MODERATION_REASONS = 'SET_MODERATION_REASONS';
const RESET_BANNER_MODERATION_INFO = 'RESET_BANNER_MODERATION_INFO';
const SET_IS_LOADING = 'SET_IS_LOADING';

interface PayloadTypes {
    [SET_CAMPAIGN_MODERATION_LIST]: CampaignModerationInfo[];
    [SET_BANNER_MODERATION_INFO]: BannerModerationInfo | null;
    [REMOVE_BANNER_FROM_MODERATION_LIST]: {
        campaignId: number;
        bannerId: number;
    };
    [SET_MODERATION_REASONS]: ModerationReason[];
    [RESET_BANNER_MODERATION_INFO]: void;
    [SET_IS_LOADING]: boolean;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaignModerationList = ActionCreator(SET_CAMPAIGN_MODERATION_LIST);
export const setBannerModerationInfo = ActionCreator(SET_BANNER_MODERATION_INFO);
export const removeBannerFromModerationList = ActionCreator(REMOVE_BANNER_FROM_MODERATION_LIST);
export const setModerationReasons = ActionCreator(SET_MODERATION_REASONS);
export const resetBannerModerationInfo = ActionCreator(RESET_BANNER_MODERATION_INFO);
export const setIsLoading = ActionCreator(SET_IS_LOADING);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
export interface BannerModerationState {
    campaignModerationList: CampaignModerationInfo[];
    bannerModerationInfo: BannerModerationInfo | null;
    moderationReasons: ModerationReason[];
    isLoading: boolean;
}

export const BANNER_MODERATION_INITIAL_STATE: BannerModerationState = {
    campaignModerationList: [],
    bannerModerationInfo: null,
    moderationReasons: [],
    isLoading: false,
};

export default createReducer<BannerModerationState, PayloadTypes>(BANNER_MODERATION_INITIAL_STATE, {
    [SET_CAMPAIGN_MODERATION_LIST]: (state, { payload }) => ({
        ...state,
        campaignModerationList: payload,
    }),
    [SET_BANNER_MODERATION_INFO]: (state, { payload }) => ({
        ...state,
        bannerModerationInfo: payload,
    }),
    [REMOVE_BANNER_FROM_MODERATION_LIST]: (state, { payload }) => removeBannerFromState(state, payload),
    [SET_MODERATION_REASONS]: (state, { payload }) => ({
        ...state,
        moderationReasons: payload,
    }),
    [RESET_BANNER_MODERATION_INFO]: (state) => ({
        ...state,
        bannerModerationInfo: null,
    }),
    [SET_IS_LOADING]: (state, { payload }) => ({
        ...state,
        isLoading: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
